import { WellspringRole } from '@metron/shared';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar.tsx';
import { Badge } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import { LogOut, Settings, User } from 'lucide-react';
import { toast } from 'sonner';

// Wellspring role groups arrive with the Wellspring- prefix already stripped
// by metron-auth; everything else (metron-internal, FlowControl-*, Cognito's
// auto-generated <pool>_<provider> group) passes through unfiltered. The
// profile badge list only shows the Wellspring roles.
const WELLSPRING_ROLES = new Set<string>(Object.values(WellspringRole));

export function UserProfile() {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    toast.success('Logged out successfully');
    logout();
  };

  if (!user) {
    return null;
  }

  const displayGroups = (user.groups ?? []).filter((group) => WELLSPRING_ROLES.has(group));

  const initials = user.name
    ? user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
    : user.email
    ? user.email[0].toUpperCase()
    : 'U';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='relative h-10 w-10 rounded-full p-0'>
          <Avatar className='h-10 w-10'>
            <AvatarImage src={undefined} alt={user.name || user.email} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56' align='end' forceMount>
        <DropdownMenuLabel className='font-normal'>
          <div className='flex flex-col space-y-2'>
            <p className='text-sm font-medium leading-none'>{user.name || 'User'}</p>
            <p className='text-xs leading-none text-muted-foreground'>
              {user.email || 'No email'}
            </p>
            {displayGroups.length > 0 && (
              <div className='flex flex-wrap gap-1 mt-1'>
                {displayGroups.map((group) => (
                  <Badge key={group} variant='secondary' className='text-xs'>
                    {group}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <User className='mr-2 h-4 w-4' />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Settings className='mr-2 h-4 w-4' />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className='mr-2 h-4 w-4' />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
